import React, { Component, ErrorInfo, ReactNode } from 'react';
import { Button } from './common/Button';
import { RefreshCw, AlertTriangle, Home, Bug } from 'lucide-react';

interface Props {
  children: ReactNode;
  fallback?: ReactNode;
  onError?: (error: Error, errorInfo: ErrorInfo) => void;
}

interface State {
  hasError: boolean;
  error: Error | null;
  errorInfo: ErrorInfo | null;
  errorId: string;
}

export class ErrorBoundary extends Component<Props, State> {
  private retryCount = 0;
  private maxRetries = 3;

  public state: State = {
    hasError: false,
    error: null,
    errorInfo: null,
    errorId: '',
  };

  public static getDerivedStateFromError(error: Error): State {
    const errorId = `error_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
    return {
      hasError: true,
      error,
      errorInfo: null,
      errorId,
    };
  }

  public componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    console.error('🚨 ErrorBoundary caught an error:', error, errorInfo);
    
    // Log detailed error information
    this.logErrorDetails(error, errorInfo);
    
    // Call parent error handler if provided
    this.props.onError?.(error, errorInfo);
    
    this.setState({
      error,
      errorInfo,
    });
  }

  private logErrorDetails = (error: Error, errorInfo: ErrorInfo) => {
    const errorDetails = {
      timestamp: new Date().toISOString(),
      userAgent: navigator.userAgent,
      url: window.location.href,
      error: {
        name: error.name,
        message: error.message,
        stack: error.stack,
      },
      errorInfo: {
        componentStack: errorInfo.componentStack,
      },
      buildInfo: {
        timestamp: document.querySelector('meta[name="build-timestamp"]')?.getAttribute('content') || 'unknown',
      },
    };

    // Store in localStorage for debugging
    try {
      localStorage.setItem(`sleepcalc_error_${this.state.errorId}`, JSON.stringify(errorDetails));
    } catch (e) {
      console.warn('Failed to store error details in localStorage:', e);
    }

    // Send to console for development
    console.group('🔍 Detailed Error Information');
    console.error('Error Details:', errorDetails);
    console.groupEnd();
  };

  private handleRetry = () => {
    if (this.retryCount < this.maxRetries) {
      this.retryCount++;
      console.log(`🔄 Retrying... (attempt ${this.retryCount}/${this.maxRetries})`);
      this.setState({
        hasError: false,
        error: null,
        errorInfo: null,
        errorId: '',
      });
    } else {
      console.warn('❌ Max retries reached, please refresh the page');
    }
  };

  private handleReload = () => {
    // Clear any cached data that might be causing issues
    try {
      localStorage.removeItem('sleepcalc-calculator');
      localStorage.removeItem('sleepcalc-ui');
      sessionStorage.clear();
    } catch (e) {
      console.warn('Failed to clear storage:', e);
    }
    
    // Force reload the page
    window.location.reload();
  };

  private handleGoHome = () => {
    window.location.href = '/';
  };

  private copyErrorDetails = () => {
    const errorText = `
SleepCalc Pro Error Report
========================
Time: ${new Date().toISOString()}
URL: ${window.location.href}
User Agent: ${navigator.userAgent}

Error: ${this.state.error?.name} - ${this.state.error?.message}

Stack Trace:
${this.state.error?.stack}

Component Stack:
${this.state.errorInfo?.componentStack}
    `.trim();

    navigator.clipboard.writeText(errorText).then(() => {
      alert('错误详情已复制到剪贴板');
    }).catch(() => {
      console.log('Error details:', errorText);
      alert('复制失败，请查看控制台获取错误详情');
    });
  };

  public render() {
    if (this.state.hasError) {
      // Custom fallback UI
      if (this.props.fallback) {
        return this.props.fallback;
      }

      // Default error UI
      return (
        <div className="min-h-screen bg-slate-50 dark:bg-slate-900 flex items-center justify-center p-4">
          <div className="max-w-md w-full bg-white dark:bg-slate-800 rounded-lg shadow-lg p-6">
            <div className="text-center mb-6">
              <div className="mx-auto w-16 h-16 bg-red-100 dark:bg-red-900/20 rounded-full flex items-center justify-center mb-4">
                <AlertTriangle className="h-8 w-8 text-red-600 dark:text-red-400" />
              </div>
              <h2 className="text-xl font-bold text-slate-900 dark:text-white mb-2">
                应用程序遇到错误
              </h2>
              <p className="text-slate-600 dark:text-slate-300 text-sm mb-4">
                抱歉，SleepCalc Pro 遇到了意外错误。这可能是临时问题。
              </p>
              <details className="text-left bg-slate-100 dark:bg-slate-700 rounded p-3 mb-4">
                <summary className="cursor-pointer text-sm font-medium text-slate-700 dark:text-slate-300 mb-2">
                  错误详情 (点击展开)
                </summary>
                <div className="text-xs text-slate-600 dark:text-slate-400 font-mono">
                  <p><strong>错误ID:</strong> {this.state.errorId}</p>
                  <p><strong>错误类型:</strong> {this.state.error?.name}</p>
                  <p><strong>错误消息:</strong> {this.state.error?.message}</p>
                  <p><strong>发生时间:</strong> {new Date().toLocaleString()}</p>
                </div>
              </details>
            </div>

            <div className="space-y-3">
              {this.retryCount < this.maxRetries && (
                <Button
                  onClick={this.handleRetry}
                  className="w-full bg-blue-600 hover:bg-blue-700 text-white"
                >
                  <RefreshCw className="h-4 w-4 mr-2" />
                  重试 ({this.maxRetries - this.retryCount} 次重试机会)
                </Button>
              )}

              <Button
                onClick={this.handleReload}
                variant="secondary"
                className="w-full"
              >
                <RefreshCw className="h-4 w-4 mr-2" />
                刷新页面
              </Button>

              <Button
                onClick={this.handleGoHome}
                variant="secondary"
                className="w-full"
              >
                <Home className="h-4 w-4 mr-2" />
                返回首页
              </Button>

              <Button
                onClick={this.copyErrorDetails}
                variant="outline"
                className="w-full"
              >
                <Bug className="h-4 w-4 mr-2" />
                复制错误详情
              </Button>
            </div>

            <div className="mt-6 text-center">
              <p className="text-xs text-slate-500 dark:text-slate-400">
                如果问题持续，请联系支持团队: support@sleepcalc.pro
              </p>
            </div>
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}

// Hook for functional components to handle errors
export const useErrorHandler = () => {
  return (error: Error, errorInfo?: any) => {
    console.error('🚨 Manual error report:', error, errorInfo);
    
    // Create a custom error event
    const errorEvent = new CustomEvent('sleepcalc:error', {
      detail: { error, errorInfo, timestamp: Date.now() }
    });
    
    window.dispatchEvent(errorEvent);
  };
};

// Global error handler for unhandled promises and errors
export const setupGlobalErrorHandling = () => {
  // Unhandled promise rejections
  window.addEventListener('unhandledrejection', (event) => {
    console.error('🚨 Unhandled Promise Rejection:', event.reason);
    console.warn('This might cause the app to appear blank!');
    
    // Prevent the default browser behavior
    event.preventDefault();
    
    // Try to show a user-friendly message
    const errorDiv = document.createElement('div');
    errorDiv.innerHTML = `
      <div style="
        position: fixed; 
        top: 20px; 
        right: 20px; 
        background: #fee2e2; 
        border: 1px solid #fecaca; 
        border-radius: 8px; 
        padding: 16px; 
        max-width: 300px; 
        z-index: 9999;
        color: #991b1b;
        font-family: system-ui, -apple-system, sans-serif;
      ">
        <strong>⚠️ 网络或加载错误</strong><br>
        <small>正在尝试恢复...</small>
      </div>
    `;
    document.body.appendChild(errorDiv);
    
    // Auto-remove after 5 seconds
    setTimeout(() => {
      if (errorDiv.parentNode) {
        errorDiv.parentNode.removeChild(errorDiv);
      }
    }, 5000);
  });

  // Global JavaScript errors
  window.addEventListener('error', (event) => {
    console.error('🚨 Global JavaScript Error:', event.error);
    console.error('Source:', event.filename, 'Line:', event.lineno, 'Column:', event.colno);
  });

  // Log when the app successfully loads
  window.addEventListener('load', () => {
    console.log('✅ SleepCalc Pro loaded successfully');
    console.log('Build timestamp:', document.querySelector('meta[name="build-timestamp"]')?.getAttribute('content'));
  });
};